import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, MapPin, Phone, MessageCircle, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { fadeUp, stagger } from "@/lib/motion";

const CONTACT_INFO = [
  {
    Icon: MapPin,
    label: "Head Office",
    value: "Lucknow, Uttar Pradesh, India",
  },
  {
    Icon: Phone,
    label: "Call us",
    value: "+91 80907 73315",
    href: "tel:+918090773315",
  },
  {
    Icon: Mail,
    label: "Email us",
    value: "Support@primetechnology.com",
    href: "mailto:Support@primetechnology.com",
  },
  {
    Icon: MessageCircle,
    label: "WhatsApp",
    value: "+91 80907 73315",
    href: "https://wa.me/918090773315",
  },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", message: "" });
  const [status, setStatus] = useState("idle"); // idle | loading | success

  const onSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setStatus("loading");

    // Build a clean WhatsApp message
    const lines = [
      "🙏 *New Enquiry — Prime Technology*",
      "",
      `👤 *Name:* ${form.name}`,
      `📧 *Email:* ${form.email}`,
      form.phone   ? `📞 *Phone:* ${form.phone}`   : null,
      form.company ? `🏢 *Company:* ${form.company}` : null,
      "",
      `💬 *Message:*`,
      form.message,
    ]
      .filter((l) => l !== null)
      .join("\n");

    const waURL = `https://wa.me/918090773315?text=${encodeURIComponent(lines)}`;

    setTimeout(() => {
      setStatus("success");
      toast.success("Redirecting to WhatsApp…");
      setForm({ name: "", email: "", phone: "", company: "", message: "" });
      window.open(waURL, "_blank");
      setTimeout(() => setStatus("idle"), 2500);
    }, 600);
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 lg:py-32 bg-[#FAF9F6] overflow-hidden">
      <div className="orb bg-[#0D47A1] w-[400px] h-[400px] top-24 -left-24 opacity-10" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          variants={stagger(0.1, 0.08)}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
          className="max-w-2xl"
        >
          <motion.span variants={fadeUp} className="eyebrow"><span className="dot" /> Get In Touch</motion.span>
          <motion.h2 variants={fadeUp} className="mt-6 font-display font-black text-4xl sm:text-5xl leading-[1.08] tracking-tight text-[#1A1A2E]">
            Let's talk about your{" "}
            <span className="font-serif-accent text-[#FFA000]">TallyPrime setup.</span>
          </motion.h2>
          <motion.p variants={fadeUp} className="mt-5 text-[17px] text-[#4A5568]">
            Share a few details and our TallyPrime experts will get back to you within 2 hours.
          </motion.p>
        </motion.div>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Left: Form - ISS template style */}
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            onSubmit={onSubmit}
            data-testid="contact-form"
            className="lg:col-span-7 rounded-2xl bg-white text-[#1A1A2E] p-8 lg:p-10 border border-[#0D47A1]/10 shadow-[0_10px_40px_rgba(13,71,161,0.08)] relative overflow-hidden"
          >
            <div className="relative">
              <h3 className="font-display font-black text-3xl tracking-tight">Get a free demo</h3>
              <p className="mt-2 text-[#4A5568] text-[15px]">Fill in your details — we'll set up a personalised walk-through.</p>

              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Full name*">
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Rohan Mehta"
                    data-testid="contact-name-input"
                    className="bg-[#FAF9F6] border-[#0D47A1]/15 text-[#1A1A2E] placeholder:text-[#8896A6] h-12 rounded-lg focus-visible:ring-[#FFA000]"
                  />
                </Field>
                <Field label="Email*">
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="rohan@company.in"
                    data-testid="contact-email-input"
                    className="bg-[#FAF9F6] border-[#0D47A1]/15 text-[#1A1A2E] placeholder:text-[#8896A6] h-12 rounded-lg focus-visible:ring-[#FFA000]"
                  />
                </Field>
                <Field label="Phone">
                  <Input
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="+91 98XXXXXXXX"
                    data-testid="contact-phone-input"
                    className="bg-[#FAF9F6] border-[#0D47A1]/15 text-[#1A1A2E] placeholder:text-[#8896A6] h-12 rounded-lg focus-visible:ring-[#FFA000]"
                  />
                </Field>
                <Field label="Company">
                  <Input
                    value={form.company}
                    onChange={(e) => setForm({ ...form, company: e.target.value })}
                    placeholder="Company name"
                    data-testid="contact-company-input"
                    className="bg-[#FAF9F6] border-[#0D47A1]/15 text-[#1A1A2E] placeholder:text-[#8896A6] h-12 rounded-lg focus-visible:ring-[#FFA000]"
                  />
                </Field>
              </div>
              <div className="mt-4">
                <Field label="How can we help?*">
                  <Textarea
                    rows={4}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="Tell us about your business and what you need — installation, migration, AMC, training..."
                    data-testid="contact-message-input"
                    className="bg-[#FAF9F6] border-[#0D47A1]/15 text-[#1A1A2E] placeholder:text-[#8896A6] rounded-lg focus-visible:ring-[#FFA000]"
                  />
                </Field>
              </div>

              <Button
                type="submit"
                disabled={status === "loading"}
                data-testid="contact-submit-button"
                className="btn-magnetic mt-6 rounded-lg h-13 px-8 bg-[#FFA000] hover:bg-[#E69000] text-white font-bold shadow-[0_15px_40px_rgba(255,160,0,0.3)]"
              >
                {status === "loading" ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Opening WhatsApp…
                  </>
                ) : status === "success" ? (
                  <>
                    <CheckCircle2 className="w-4 h-4 mr-2" /> Sent via WhatsApp
                  </>
                ) : (
                  <>
                    <MessageCircle className="w-4 h-4 mr-2" /> Send on WhatsApp
                  </>
                )}
              </Button>

              <p className="mt-4 text-[12px] text-[#8896A6]">
                Your details will open as a pre-filled WhatsApp message to our team at +91 80907 73315.
              </p>
            </div>
          </motion.form>

          {/* Right: Info + Image - ISS template style */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="lg:col-span-5 space-y-5"
          >
            {/* Team collaboration image */}
            <div className="rounded-2xl overflow-hidden border border-[#0D47A1]/10 shadow-[0_10px_40px_rgba(13,71,161,0.1)] aspect-[4/3]">
              <img
                src="https://images.pexels.com/photos/6779567/pexels-photo-6779567.jpeg?auto=compress&cs=tinysrgb&w=1200"
                alt="Team collaboration"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>

            <div className="rounded-2xl bg-[#0D47A1] text-white p-6 space-y-4">
              {CONTACT_INFO.map(({ Icon, label, value, href }, i) => {
                const content = (
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-xl bg-white/10 border border-white/20 flex items-center justify-center shrink-0">
                      <Icon className="w-5 h-5 text-[#FFA000]" strokeWidth={1.75} />
                    </div>
                    <div>
                      <div className="text-[11px] tracking-[0.14em] uppercase font-bold text-white/60">{label}</div>
                      <div className="mt-0.5 text-[15px] font-semibold text-white">{value}</div>
                    </div>
                  </div>
                );
                return href ? (
                  <a key={label} href={href} data-testid={`contact-info-${i}`} className="block hover:bg-white/5 rounded-xl p-2 -m-2 transition">
                    {content}
                  </a>
                ) : (
                  <div key={label} data-testid={`contact-info-${i}`}>{content}</div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[12px] font-semibold tracking-[0.08em] uppercase text-[#0D47A1]/70">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
